import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { options as selectOptions } from 'activity-templates/utils';
import SelectGroup from 'components/flink-components/ActivityOptions/SelectGroup';
import { ColorPickerField } from 'components/common';

// R02 (2026-10-24): Flash-Cards-LOCAL font-size list + saved-value migration
// (React-free, unit-tested). Do NOT change shared activity-templates/utils/options.js.
import { FC_FONT_SIZES, fontSizeOptions } from './fontSizeOptions';
import classes from './FlashCardsOptions.module.scss';

const isAbsent = (v) => v === undefined || v === null;

const firstOf = (arr) => {
  const f = arr && arr[0];
  return f && typeof f === 'object' ? f.value : f;
};

// Confirmed Flash Cards defaults (FC-09 / FC-10). These win over SelectGroup's
// implicit first-option (which would otherwise give delay=1, showAnswer='n/a').
const FC_DEFAULTS = {
  delayBeforeNext: 3,
  showAnswer: '2',
  randomOrder: false,
};

// Build the resolved default for one key: a saved/author default takes priority,
// then the Flash Cards policy default, then the shared array's first option, so
// NO SelectGroup child ever sees an absent value and self-initializes.
const resolveDefault = (key, defaultOptions) => {
  if (!isAbsent(defaultOptions[key])) return defaultOptions[key];
  if (!isAbsent(FC_DEFAULTS[key])) return FC_DEFAULTS[key];
  switch (key) {
    case 'fontFamily':
      return firstOf(selectOptions.fontFamily);
    case 'fontSize':
      // R02: default to the Flash-Cards-local list's first size, NOT the shared 12.
      return FC_FONT_SIZES[0];
    default:
      return undefined;
  }
};

const CONTROLLED_KEYS = ['delayBeforeNext', 'showAnswer', 'randomOrder', 'fontFamily', 'fontSize'];

/**
 * Flash-Cards-local Options form. Reuses the shared generic primitives
 * (SelectGroup, ColorPickerField) and the shared supported-value arrays; it does
 * NOT redesign global ActivityOptions. Rendered via the OptionsTab plugin-Options
 * seam.
 *
 * SelectGroup self-initializes any `current[name]` that is undefined/null by
 * dispatching its FIRST option (delay=1, showAnswer='n/a'). To prevent those
 * conflicting writes AND to prevent them from overriding the Flash Cards policy
 * defaults, every SelectGroup is given a RESOLVED `current` object whose missing
 * keys are already filled with the resolved default. The same resolved defaults
 * are persisted ONCE for genuinely-absent keys; every existing saved value is
 * preserved (we only fill keys that are undefined/null).
 */
const FlashCardsOptions = ({ options = {}, changeHandler, translate, defaultOptions = {} }) => {
  // Resolved view handed to the controlled children — never has an absent key, so
  // no child's first-option effect fires.
  const resolved = useMemo(() => {
    const next = { ...options };
    CONTROLLED_KEYS.forEach((key) => {
      if (isAbsent(next[key])) {
        const d = resolveDefault(key, defaultOptions);
        if (!isAbsent(d)) next[key] = d;
      }
    });
    return next;
  }, [options, defaultOptions]);

  // Persist the resolved defaults for genuinely-absent keys exactly once, so the
  // saved activity + Play see the same values the form shows. Saved values are
  // untouched (only absent keys are written).
  useEffect(() => {
    CONTROLLED_KEYS.forEach((key) => {
      if (isAbsent(options[key])) {
        const d = resolveDefault(key, defaultOptions);
        if (!isAbsent(d)) changeHandler(key, d);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const randomOrder = !!resolved.randomOrder;

  return (
    <div className={classes.form}>
      {/* Delay before next */}
      <section className={classes.group}>
        <b className={classes.legend}>
          {translate(456, 'Length of time before continuing to next question:')}
        </b>
        <SelectGroup
          translate={translate}
          label={translate(460, 'Seconds')}
          options={selectOptions.delayBeforeNext}
          current={resolved}
          name="delayBeforeNext"
          changeHandler={changeHandler}
          numeric
        />
      </section>

      {/* Font: Type / Size / Color on one aligned row */}
      <section className={classes.group}>
        <b className={classes.legend}>{translate(369, 'Font:')}</b>
        <div className={classes.fontRow}>
          <SelectGroup
            translate={translate}
            label={translate(453, 'Type:')}
            options={selectOptions.fontFamily}
            current={resolved}
            name="fontFamily"
            changeHandler={changeHandler}
          />
          <SelectGroup
            translate={translate}
            label={translate(454, 'Size:')}
            options={fontSizeOptions(resolved.fontSize)}
            current={resolved}
            name="fontSize"
            changeHandler={changeHandler}
            numeric
          />
          <ColorPickerField
            label={translate(455, 'Color:')}
            currentColor={options.fontColor}
            changeHandler={(color) => changeHandler('fontColor', color.hex)}
          />
        </div>
      </section>

      {/* Problem Order: As Written / Random radios -> randomOrder boolean */}
      <section className={classes.group}>
        <b className={classes.legend}>{translate(0, 'Problem Order:')}</b>
        <div className={classes.radioRow}>
          <label className={classes.radio}>
            <input
              type="radio"
              name="fc-random-order"
              checked={!randomOrder}
              onChange={() => changeHandler('randomOrder', false)}
            />
            {translate(0, 'As Written')}
          </label>
          <label className={classes.radio}>
            <input
              type="radio"
              name="fc-random-order"
              checked={randomOrder}
              onChange={() => changeHandler('randomOrder', true)}
            />
            {translate(0, 'Random')}
          </label>
        </div>
      </section>

      {/* Show Answer: number of tries (n/a included) */}
      <section className={classes.group}>
        <b className={classes.legend}>{translate(462, 'Show Answer:')}</b>
        <SelectGroup
          translate={translate}
          label={translate(463, 'Number of tries:')}
          options={selectOptions.showAnswer}
          current={resolved}
          name="showAnswer"
          changeHandler={changeHandler}
        />
      </section>

      {/* R11: Fill-in Case sensitive — default UNCHECKED (case-insensitive). When
          absent/false the Fill-in comparison folds case; when true it is exact. No
          default is persisted (unchecked == absent), so existing activities keep
          the new case-insensitive default without a destructive rewrite. */}
      <section className={classes.group}>
        <label className={classes.radio}>
          <input
            type="checkbox"
            checked={!!resolved.caseSensitive}
            onChange={(e) => changeHandler('caseSensitive', e.target.checked)}
          />
          {translate(0, 'Case sensitive')}
        </label>
      </section>
    </div>
  );
};

FlashCardsOptions.propTypes = {
  options: PropTypes.object,
  changeHandler: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  defaultOptions: PropTypes.object,
};

export default FlashCardsOptions;
